import { useState, useEffect, useCallback } from "react";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CREW_POSITIONS = ["General", "Painter", "Flooring", "Cleaner", "Deep Cleaner", "Trash-Out", "Plumber", "Electrician", "Handyman"];
const RECURRENCE_OPTIONS = [
  { value: "", label: "One-time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
];

const OperationsTab = () => (
  <div className="dash-tab-content" data-testid="operations-tab">
    <h2>The Precision Wave Strategy</h2>
    <p className="ops-lead">Rolling Asset Delivery — a continuous pipeline of "Ready-to-Rent" units rather than a single completion date.</p>

    <div className="ops-waves">
      <div className="ops-wave" data-testid="ops-48hr">
        <div className="ops-wave-header ops-express"><span className="ops-badge">48-HOUR WAVE</span><h3>Light Swift-Turn</h3></div>
        <p>Immediate execution of kitchen refreshes and modular hardware swaps. Low-friction units back to market status instantly.</p>
        <ul className="ops-list">
          <li>Debris removal and cleanout</li>
          <li>Touch-up paint, hardware swap</li>
          <li>Light fixture replacement</li>
          <li>NSPIRE/HQS compliance check</li>
          <li>Photo documentation</li>
        </ul>
      </div>
      <div className="ops-wave" data-testid="ops-14day">
        <div className="ops-wave-header ops-standard"><span className="ops-badge">14-DAY TARGET</span><h3>Volume Stabilization</h3></div>
        <p>Primary bracket for multi-unit requests. 14-day window with professional margin — efficiency typically delivers 3 units in a 7-day forecast.</p>
        <ul className="ops-list">
          <li>Full paint — walls, ceilings, accents (2 coats)</li>
          <li>LVP/VGT flooring throughout</li>
          <li>Cabinet resurface + modern hardware</li>
          <li>Fixture and faucet upgrade</li>
          <li>NSPIRE/HQS compliance verified</li>
        </ul>
      </div>
      <div className="ops-wave" data-testid="ops-21day">
        <div className="ops-wave-header ops-heavy"><span className="ops-badge">21-DAY WAVE</span><h3>Heavy Recovery</h3></div>
        <p>Deep restoration for legacy or offline units. Transforming high-vacancy assets into market-ready builds at a fraction of renovation costs.</p>
        <ul className="ops-list">
          <li>Full interior overhaul</li>
          <li>Tile, backsplash, countertop refresh</li>
          <li>Tiered variation system per unit type</li>
          <li>Boutique-grade finishes at modular cost</li>
          <li>Property Showcase documentation</li>
        </ul>
      </div>
    </div>

    <div className="ops-section" data-testid="ops-materials">
      <h2>High-Performance Material Logic</h2>
      <div className="ops-cards">
        <div className="ops-card">
          <h4>Acoustic LVP Flooring</h4>
          <p>High-density foam backing for noise reduction in multi-story units. Individual plank replacement eliminates full-room carpet rip-and-replace cycles.</p>
        </div>
        <div className="ops-card">
          <h4>Washable Surface Technology</h4>
          <p>Satin architectural coatings engineered to withstand deep cleaning of organic residue. Surface restoration over full-scale repaints between turns.</p>
        </div>
        <div className="ops-card">
          <h4>Tiered Aesthetic Palettes</h4>
          <p>Custom-feel kits — The Sierra (1BR), Coastal Earth (2BR) — modernize legacy properties to meet shifting market demands.</p>
        </div>
      </div>
    </div>

    <div className="ops-section" data-testid="ops-vendor">
      <h2>Strategic Sourcing & Vendor Optimization</h2>
      <div className="ops-cards">
        <div className="ops-card">
          <h4>Direct Resource Management</h4>
          <p>Established accounts with Sherwin-Williams, HD Supply, Ferguson, and Lowe's. Quality materials at cost-effective bulk inventory pricing for every job.</p>
        </div>
        <div className="ops-card">
          <h4>Independent Specialist Sourcing</h4>
          <p>We bypass the "commercial brand tax" by sourcing verified, independent specialists for repairs (pool restoration, exterior lighting, etc.).</p>
        </div>
        <div className="ops-card">
          <h4>Professional Representation</h4>
          <p>All sourced specialists operate under our brand oversight and wear a Moovin Forward vest on-site — unified, professional presence.</p>
        </div>
      </div>
    </div>

    <div className="ops-section" data-testid="ops-partnership">
      <h2>Parallel-Workstream Stabilization Framework</h2>
      <p className="ops-note">Every unit Moovin Forward touches is a home someone is stepping into for the first time. For that first impression to hold, two workstreams have to close on the same day: the property&rsquo;s on-site maintenance staff restores the building-envelope condition &mdash; fa&ccedil;ade, grounds, ingress, shared systems &mdash; while Moovin Forward LLC delivers the interior side to NSPIRE, VAWA/EHV, HUD-VASH, and Section 8 readiness. <strong>Neither team works for the other; both teams work for the person moving in.</strong> When both close together, the property earns its market-ready standing and unlocks the fastest lawful placement path &mdash; including voucher, veteran, and coalition-referred tenancies.</p>
      <p className="ops-note"><strong>Positioning line for pitch decks and receiver calls:</strong> &ldquo;Moovin Forward LLC identifies the interior-side stabilization gaps that erode Time-to-Lease and NSPIRE pass-rate. We deliver the inspection-ready interior; the property&rsquo;s on-site maintenance staff delivers the building-envelope condition. When both workstreams close on the same date, the property achieves full market-readiness &mdash; and a veteran, survivor, or voucher family walks into a home that meets them with dignity from the curb through the doorway.&rdquo;</p>
    </div>

    <div className="ops-section" data-testid="ops-receivership">
      <h2>Receivership & Distressed Asset Playbook</h2>
      <p className="ops-lead">Court-appointed receivers manage $826M+ in distressed DFW multifamily loans. They need turnover partners who document everything and stay on budget. This is our highest-value client segment.</p>
      <div className="ops-cards">
        <div className="ops-card">
          <h4>Why Receivers Choose Us</h4>
          <p>Court-grade Line-Itemized Packets match court-approved budgets line by line. The 50/50 model means materials are funded upfront — zero cost overrun risk. NSPIRE-Ready Checklists prove compliance.</p>
        </div>
        <div className="ops-card">
          <h4>The Receiver Pitch</h4>
          <p>"Our Activation Capital model funds materials on Day 1 within your court-approved budget. Every unit gets a Line-Itemized Packet and NSPIRE-Ready Checklist — your documentation for the court. Stabilization Fee is only due after your sign-off."</p>
        </div>
        <div className="ops-card">
          <h4>Active DFW Pipeline</h4>
          <p>Dallas County: $280M+ in multifamily loans at auction. Major complexes: 424-unit to 650-unit properties in receivership. Tarrant County: 444-unit and smaller properties cycling through. Contact the District Clerk to register as an approved vendor.</p>
        </div>
      </div>
    </div>
  </div>
);


const ComplianceTab = () => (
  <div className="dash-tab-content" data-testid="compliance-tab">
    <h2>Project Documentation & Compliance</h2>
    <p className="ops-lead">Every unit completed by Moovin Forward includes a Line-Itemized Packet and an NSPIRE-Ready Checklist — a certified record of the unit's condition and safety standards at completion.</p>

    <div className="ops-section" data-testid="compliance-legal">
      <h2>Legal Protections</h2>
      <p>In the event of a habitability dispute or court proceeding, this packet provides verifiable evidence that all items were inspected, corrected, and signed off on prior to occupancy. This protects the property owner against claims that the unit was substandard or failed to meet codes at time of move-in.</p>
      <div className="ops-cards">
        <div className="ops-card">
          <h4>Code Compliance Verification</h4>
          <p>Hard evidence that the unit met all specified safety and aesthetic standards at the time of turnover.</p>
        </div>
        <div className="ops-card">
          <h4>Occupancy Dispute Defense</h4>
          <p>Signed record that serves as a rebuttal against claims that a unit was not "up to code" or "rent-ready" at the start of a lease.</p>
        </div>
        <div className="ops-card">
          <h4>Eviction Support</h4>
          <p>Clear, chronological documentation of the unit's condition to streamline the retrieval of possession or resolve disputes regarding property standards.</p>
        </div>
      </div>
    </div>

    <div className="ops-section" data-testid="compliance-process">
      <h2>Unit Status Documentation Process</h2>
      <div className="compliance-steps">
        <div className="comp-step" data-testid="comp-step-1">
          <span className="comp-step-num">1</span>
          <div>
            <h4>Point of Intake</h4>
            <p>Signed Commencement Approval — documents the exact physical state of the unit at handoff. Full photo record of every surface, fixture, and system.</p>
          </div>
        </div>
        <div className="comp-step" data-testid="comp-step-2">
          <span className="comp-step-num">2</span>
          <div>
            <h4>Line-Itemized Execution</h4>
            <p>Every material, labor item, and scope change documented on a dedicated Project Card. Full transparency for owners and receivers.</p>
          </div>
        </div>
        <div className="comp-step" data-testid="comp-step-3">
          <span className="comp-step-num">3</span>
          <div>
            <h4>Final Delivery Sign-Off</h4>
            <p>Completion Approval — owner or authorized rep verifies the unit meets the approved scope. NSPIRE-Ready Checklist attached. Stabilization Fee triggered on signature.</p>
          </div>
        </div>
      </div>
    </div>

    <div className="ops-section" data-testid="compliance-insurance">
      <h2>Vendor Insurance & Liability</h2>
      <div className="ops-cards">
        <div className="ops-card">
          <h4>Verified Partnerships</h4>
          <p>Every specialist sourced by Moovin Forward maintains an up-to-date professional license and provides proof of active insurance coverage.</p>
        </div>
        <div className="ops-card">
          <h4>Liability Separation</h4>
          <p>All partners sign a formal contract acknowledging they are liable for their own scope of work. Mistakes or breakdowns are covered under their own primary insurance policies.</p>
        </div>
      </div>
    </div>

    <div className="ops-section" data-testid="compliance-disclosure">
      <h2>Service &amp; Compliance Disclosure</h2>
      <p className="ops-note">Moovin Forward LLC is a fixed-fee interior unit stabilization operator. We are not a real estate brokerage, property management firm, or leasing agent as defined under Tex. Occ. Code Ch. 1101. Our scope is confined to the interior condition of the unit: NSPIRE-graded finishes, HQS-graded fixtures, VAWA/EHV survivor-appropriate design, and inspection-ready documentation. Where our interior work adjoins an exterior or building-envelope condition that falls outside our scope, we deliver a written condition note to the property&rsquo;s on-site maintenance staff or asset manager, referencing the applicable NSPIRE, HUD, or municipal standard so their team can close the exterior gap on their own timeline and under their own contract. We do not direct, supervise, or invoice for exterior work.</p>
    </div>

    <div className="ops-section" data-testid="admin-setup-guide">
      <h2>Admin Setup Guide — Email Notifications</h2>
      <p className="ops-lead">Right now email notifications only go to nivoomforward@gmail.com (Resend test mode). Follow these steps to enable notifications for all clients and team members:</p>
      <div className="compliance-steps">
        <div className="comp-step"><span className="comp-step-num">1</span><div><h4>Log into Resend</h4><p>Go to <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" style={{color: '#00ffff'}}>resend.com/domains</a> and sign in with the account that has our API key.</p></div></div>
        <div className="comp-step"><span className="comp-step-num">2</span><div><h4>Add Your Domain</h4><p>Click "Add Domain" and enter the business domain (e.g., moovinforward.org). You must own this domain.</p></div></div>
        <div className="comp-step"><span className="comp-step-num">3</span><div><h4>Copy the DNS Records</h4><p>Resend will show 3-4 DNS records (TXT and CNAME). Copy each one exactly.</p></div></div>
        <div className="comp-step"><span className="comp-step-num">4</span><div><h4>Add Records to Domain Provider</h4><p>Go to your domain registrar (GoDaddy, Namecheap, Google Domains, etc.). Navigate to DNS settings and add each record Resend provided.</p></div></div>
        <div className="comp-step"><span className="comp-step-num">5</span><div><h4>Verify in Resend</h4><p>Go back to Resend and click "Verify." DNS propagation takes a few minutes. Once verified, emails can be sent FROM your domain (e.g., updates@moovinforward.org) TO anyone — clients, team, brokers.</p></div></div>
      </div>
      <p className="ops-note"><strong>After verification:</strong> Update the SENDER_EMAIL in the backend .env file to your new verified address (e.g., updates@moovinforward.org). Phase change notifications will then reach all clients automatically.</p>
    </div>
  </div>
);


// ===== Quick Start Welcome =====
const QuickStartWelcome = ({ user, onDismiss }) => (
  <div className="quickstart-overlay" data-testid="quickstart-welcome">
    <div className="quickstart-modal">
      <div className="quickstart-header">
        <div>
          <h2>Welcome to Moovin Forward HQ, {user.name?.split(" ")[0] || "Team"}</h2>
          <p className="quickstart-sub">Here's what each tab does and where to start.</p>
        </div>
        <button onClick={onDismiss} className="quickstart-close" data-testid="quickstart-close">&times;</button>
      </div>
      <div className="quickstart-grid" data-testid="quickstart-grid">
        <div className="qs-card">
          <span className="qs-icon">1</span>
          <h4>Field Tools</h4>
          <p>Audit checklists and on-site scripts. Use this when walking a property — check items off and download a PDF for the client.</p>
          <span className="qs-action">First action: Walk a unit and fill out the checklist</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">2</span>
          <h4>Contracts</h4>
          <p>All 9 legal templates with plain-English explanations. Each one tells you what it is and when to use it. Download as PDF, fill in the blanks, get signatures. The Business Structure and Trademark documents pull live data.</p>
          <span className="qs-action">First action: Download the MSA and review it</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">3</span>
          <h4>Operations</h4>
          <p>The Precision Wave Strategy — our internal playbook for how we execute stabilizations. Material specs, vendor strategy, and the receivership approach guide.</p>
          <span className="qs-action">First action: Read the 3 wave types to know our timelines</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">4</span>
          <h4>Compliance</h4>
          <p>Documentation standards, legal protections, and vendor insurance requirements. Also has the email setup guide for enabling notifications.</p>
          <span className="qs-action">First action: Review the 3-step documentation process</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">5</span>
          <h4>Projects</h4>
          <p>Where active client projects live. You can update status, post progress photos, change phases (triggers email to client), and manage documents and invoices.</p>
          <span className="qs-action">First action: Projects appear when clients submit through the portal</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">6</span>
          <h4>Clients</h4>
          <p>Everyone who has signed in through the portal. You can promote people to Team or Admin, or keep them as Client. Your role controls what they see.</p>
          <span className="qs-action">First action: Check who has signed in and assign roles</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">7</span>
          <h4>Payroll</h4>
          <p>Track per-unit crew payouts across all projects. Set rates inside each project, mark payments (Zelle/Cash/Check), and monitor totals for 1099 reporting.</p>
          <span className="qs-action">First action: Assign crew to a project, then set their payout rate</span>
        </div>
        <div className="qs-card">
          <span className="qs-icon">8</span>
          <h4>Tasks</h4>
          <p>Your team task board. Create tasks, assign them, set priorities and due dates. Track what's To Do, In Progress, and Done.</p>
          <span className="qs-action">First action: Create a task and assign it to a team member</span>
        </div>
      </div>
      <div className="quickstart-footer">
        <button onClick={onDismiss} className="quickstart-go-btn" data-testid="quickstart-go-btn">Got it — let's go</button>
      </div>
    </div>
  </div>
);


const PayrollTab = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/payroll`, { credentials: "include" });
        if (r.ok) setSummary(await r.json());
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportStart) params.set("start_date", exportStart);
      if (exportEnd) params.set("end_date", exportEnd);
      const r = await fetch(`${BACKEND_URL}/api/admin/payroll/export?${params}`, { credentials: "include" });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const disp = r.headers.get("Content-Disposition") || "";
        const match = disp.match(/filename="(.+?)"/);
        a.download = match ? match[1] : "payroll-export.csv";
        a.href = url;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error("Export failed:", e); }
    setExporting(false);
  };

  if (loading) return <div className="dash-loading">Loading payroll...</div>;

  const grandTotal = summary.reduce((s, c) => s + c.total_earned, 0);
  const grandPaid = summary.reduce((s, c) => s + c.total_paid, 0);
  const grandOwed = summary.reduce((s, c) => s + c.total_owed, 0);

  return (
    <div className="dash-tab-content" data-testid="payroll-tab">
      <h2>Crew Payroll Tracker</h2>
      <p className="payroll-tab-desc">1099 contractor payment summary across all projects. Set rates per-project inside each project detail view.</p>

      {/* Global Totals */}
      <div className="payroll-global-bar" data-testid="payroll-global-summary">
        <div className="payroll-global-stat">
          <span className="payroll-global-label">Total Earned</span>
          <span className="payroll-global-value">${grandTotal.toFixed(2)}</span>
        </div>
        <div className="payroll-global-stat">
          <span className="payroll-global-label">Total Paid</span>
          <span className="payroll-global-value payroll-paid">${grandPaid.toFixed(2)}</span>
        </div>
        <div className="payroll-global-stat">
          <span className="payroll-global-label">Outstanding</span>
          <span className="payroll-global-value payroll-owed">${grandOwed.toFixed(2)}</span>
        </div>
        <div className="payroll-global-stat">
          <span className="payroll-global-label">Crew Members</span>
          <span className="payroll-global-value">{summary.length}</span>
        </div>
      </div>

      {/* Export CSV */}
      <div className="payroll-export-bar" data-testid="payroll-export">
        <div className="payroll-export-left">
          <label className="payroll-export-label">Export Paid Records</label>
          <div className="payroll-date-range">
            <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} className="payroll-date-input" data-testid="export-start-date" />
            <span className="payroll-date-sep">to</span>
            <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} className="payroll-date-input" data-testid="export-end-date" />
          </div>
        </div>
        <button onClick={exportCSV} disabled={exporting} className="payroll-export-btn" data-testid="export-csv-btn">
          {exporting ? "Exporting..." : "Download CSV"}
        </button>
      </div>

      {summary.length === 0 ? (
        <div className="dash-empty">No payroll records yet. Assign crew to projects and set payout rates from each project's detail view.</div>
      ) : (
        <div className="payroll-crew-list" data-testid="payroll-crew-list">
          {summary.map(crew => (
            <div key={crew.crew_user_id} className="payroll-crew-card" data-testid={`crew-summary-${crew.crew_user_id}`}>
              <div className="payroll-crew-header">
                <div className="payroll-crew-identity">
                  <strong>{crew.crew_name || "Unknown"}</strong>
                  {crew.crew_email && <span className="payroll-email">{crew.crew_email}</span>}
                </div>
                <div className="payroll-crew-totals">
                  <span className="payroll-crew-total">Total: ${crew.total_earned.toFixed(2)}</span>
                  <span className="payroll-crew-paid">Paid: ${crew.total_paid.toFixed(2)}</span>
                  {crew.total_owed > 0 && <span className="payroll-crew-owed">Owed: ${crew.total_owed.toFixed(2)}</span>}
                </div>
              </div>
              <div className="payroll-crew-projects">
                <span className="payroll-projects-label">{crew.total_units} unit{crew.total_units !== 1 ? "s" : ""} across {crew.projects.length} assignment{crew.projects.length !== 1 ? "s" : ""}</span>
                <div className="payroll-project-list">
                  {crew.projects.map((proj, i) => (
                    <div key={i} className={`payroll-project-item ${proj.status}`}>
                      <span className="payroll-proj-id">#{proj.project_id.slice(-6)}</span>
                      <span className="payroll-proj-rate">${proj.rate.toFixed(2)}</span>
                      <span className={`payroll-status-badge ${proj.status}`}>{proj.status === "paid" ? "Paid" : "Pending"}</span>
                      {proj.paid_at && <span className="payroll-proj-date">{new Date(proj.paid_at).toLocaleDateString()}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 1099 Notice */}
      <div className="payroll-1099-notice" data-testid="payroll-1099-notice">
        <strong>1099 Reporting Reminder</strong>
        <p>All crew members paid $600+ in a calendar year must receive a 1099-NEC by January 31. Keep payment records up to date — paid amounts and methods are tracked above for each crew member.</p>
      </div>
    </div>
  );
};


const TaskBoardTab = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "medium", due_date: "", assigned_to: "" });
  const [filter, setFilter] = useState("all");

  const loadTasks = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/tasks`, { credentials: "include" });
      if (r.ok) setTasks(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    await fetch(`${BACKEND_URL}/api/admin/tasks`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(newTask)
    });
    setNewTask({ title: "", description: "", priority: "medium", due_date: "", assigned_to: "" });
    setShowAdd(false);
    loadTasks();
  };

  const updateStatus = async (taskId, status) => {
    await fetch(`${BACKEND_URL}/api/admin/tasks/${taskId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ status })
    });
    loadTasks();
  };

  const deleteTask = async (taskId) => {
    await fetch(`${BACKEND_URL}/api/admin/tasks/${taskId}`, { method: "DELETE", credentials: "include" });
    loadTasks();
  };

  if (loading) return <div className="dash-loading">Loading tasks...</div>;

  const todoTasks = tasks.filter(t => t.status === "todo");
  const inProgressTasks = tasks.filter(t => t.status === "in_progress");
  const doneTasks = tasks.filter(t => t.status === "done");

  const filtered = filter === "all" ? tasks :
    filter === "todo" ? todoTasks :
    filter === "in_progress" ? inProgressTasks : doneTasks;

  const priorityColor = (p) => p === "high" ? "priority-high" : p === "low" ? "priority-low" : "priority-med";

  return (
    <div className="dash-tab-content" data-testid="tasks-tab">
      <div className="tasks-header">
        <h2>Task Board</h2>
        <button onClick={() => setShowAdd(true)} className="tasks-add-btn" data-testid="add-task-btn">+ New Task</button>
      </div>

      {/* Summary Bar */}
      <div className="tasks-summary" data-testid="tasks-summary">
        <button className={`tasks-filter-btn ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")} data-testid="filter-all">
          All <span className="tasks-count">{tasks.length}</span>
        </button>
        <button className={`tasks-filter-btn filter-todo ${filter === "todo" ? "active" : ""}`} onClick={() => setFilter("todo")} data-testid="filter-todo">
          To Do <span className="tasks-count">{todoTasks.length}</span>
        </button>
        <button className={`tasks-filter-btn filter-progress ${filter === "in_progress" ? "active" : ""}`} onClick={() => setFilter("in_progress")} data-testid="filter-progress">
          In Progress <span className="tasks-count">{inProgressTasks.length}</span>
        </button>
        <button className={`tasks-filter-btn filter-done ${filter === "done" ? "active" : ""}`} onClick={() => setFilter("done")} data-testid="filter-done">
          Done <span className="tasks-count">{doneTasks.length}</span>
        </button>
      </div>

      {/* Add Task Form */}
      {showAdd && (
        <div className="tasks-add-form" data-testid="task-add-form">
          <input value={newTask.title} onChange={e => setNewTask(p => ({...p, title: e.target.value}))} placeholder="Task title..." className="task-input task-title-input" data-testid="task-title-input" />
          <textarea value={newTask.description} onChange={e => setNewTask(p => ({...p, description: e.target.value}))} placeholder="Description (optional)..." className="task-input task-desc-input" rows={2} data-testid="task-desc-input" />
          <div className="task-form-row">
            <select value={newTask.priority} onChange={e => setNewTask(p => ({...p, priority: e.target.value}))} className="task-select" data-testid="task-priority-select">
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
            <input type="date" value={newTask.due_date} onChange={e => setNewTask(p => ({...p, due_date: e.target.value}))} className="task-date-input" data-testid="task-due-date" />
            <input value={newTask.assigned_to} onChange={e => setNewTask(p => ({...p, assigned_to: e.target.value}))} placeholder="Assign to..." className="task-input task-assign-input" data-testid="task-assign-input" />
          </div>
          <div className="task-form-actions">
            <button onClick={addTask} className="task-save-btn" data-testid="task-save-btn">Add Task</button>
            <button onClick={() => setShowAdd(false)} className="task-cancel-btn" data-testid="task-cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      {/* Task List */}
      {filtered.length === 0 ? (
        <div className="dash-empty" data-testid="tasks-empty">
          {filter === "all" ? "No tasks yet. Click '+ New Task' to create one." : `No ${filter === "todo" ? "to-do" : filter === "in_progress" ? "in-progress" : "completed"} tasks.`}
        </div>
      ) : (
        <div className="tasks-list" data-testid="tasks-list">
          {filtered.map(task => (
            <div key={task.task_id} className={`task-card task-${task.status}`} data-testid={`task-${task.task_id}`}>
              <div className="task-card-left">
                {task.status !== "done" ? (
                  <button
                    onClick={() => updateStatus(task.task_id, task.status === "todo" ? "in_progress" : "done")}
                    className={`task-check ${task.status === "in_progress" ? "task-check-progress" : ""}`}
                    data-testid={`task-advance-${task.task_id}`}
                    title={task.status === "todo" ? "Start task" : "Complete task"}
                  />
                ) : (
                  <button
                    onClick={() => updateStatus(task.task_id, "todo")}
                    className="task-check task-check-done"
                    data-testid={`task-reopen-${task.task_id}`}
                    title="Reopen task"
                  />
                )}
              </div>
              <div className="task-card-body">
                <div className="task-card-top-row">
                  <span className={`task-title ${task.status === "done" ? "task-title-done" : ""}`}>{task.title}</span>
                  <div className="task-badges">
                    <span className={`task-priority-badge ${priorityColor(task.priority)}`}>{task.priority}</span>
                    <span className={`task-status-badge task-status-${task.status}`}>
                      {task.status === "todo" ? "To Do" : task.status === "in_progress" ? "In Progress" : "Done"}
                    </span>
                  </div>
                </div>
                {task.description && <p className="task-description">{task.description}</p>}
                <div className="task-meta">
                  {task.assigned_to && <span className="task-assigned">Assigned: {task.assigned_to}</span>}
                  {task.due_date && <span className={`task-due ${new Date(task.due_date) < new Date() && task.status !== "done" ? "task-overdue" : ""}`}>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                  <span className="task-created">Added by {task.created_by} on {new Date(task.created_at).toLocaleDateString()}</span>
                  {task.completed_at && <span className="task-completed-date">Completed: {new Date(task.completed_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="task-card-actions">
                {task.status === "in_progress" && <button onClick={() => updateStatus(task.task_id, "todo")} className="task-action-btn" data-testid={`task-back-${task.task_id}`}>Back</button>}
                {task.status !== "done" && <button onClick={() => updateStatus(task.task_id, "done")} className="task-action-btn task-done-btn" data-testid={`task-complete-${task.task_id}`}>Done</button>}
                <button onClick={() => deleteTask(task.task_id)} className="task-action-btn task-delete-btn" data-testid={`task-delete-${task.task_id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== Launch Readiness Tab (Tab 9) =====
const LAUNCH_CATEGORIES = [
  {
    category: "Legal & Entity",
    items: [
      { id: "ccorp", label: "Business entity formed (DBA → LLC → C-Corp as you scale)", cost: "$100-$300" },
      { id: "ein", label: "EIN obtained from IRS (SS-4 filed)", cost: "Free" },
      { id: "bank", label: "Business bank account opened (separate from personal)", cost: "Free" },
      { id: "trademark_roi", label: "Trademark filed: ROI STABILIZATION MODEL (USPTO Class 37)", cost: "$350" },
      { id: "trademark_pws", label: "Trademark filed: PRECISION WAVE STRATEGY", cost: "$350" },
      { id: "trademark_mf", label: "Trademark filed: MOOVIN FORWARD", cost: "$350" },
      { id: "attorney_review", label: "Contracts reviewed by attorney (MSA, IC Agreement, NDA)", cost: "$500-$1,500" },
      { id: "registered_agent", label: "Registered agent designated for service of process", cost: "$100-$300/yr" },
    ]
  },
  {
    category: "Insurance & Compliance",
    items: [
      { id: "gl_insurance", label: "General Liability insurance active (min $1M)", cost: "$800-$2,000/yr" },
      { id: "workers_comp", label: "Workers comp exclusion letter (1099 model, no W-2 employees)", cost: "Free" },
      { id: "coi_template", label: "Certificate of Insurance template ready for clients", cost: "Free" },
      { id: "nda_signed", label: "NDA template signed with first crew member", cost: "Free" },
      { id: "ic_agreement", label: "Independent Contractor Agreement template ready", cost: "Free" },
    ]
  },
  {
    category: "Operations & Vendor Accounts",
    items: [
      { id: "sw_pro", label: "Sherwin-Williams Pro account (commercial pricing)", cost: "Free" },
      { id: "hd_supply", label: "HD Supply account (bulk fixtures, hardware)", cost: "Free" },
      { id: "ferguson", label: "Ferguson account (plumbing, commercial grade)", cost: "Free" },
      { id: "home_depot_pro", label: "Home Depot Pro Xtra account", cost: "Free" },
      { id: "vehicle", label: "Work vehicle ready (wrap optional)", cost: "$0-$2,500" },
      { id: "tools", label: "Professional tool kit assembled (drill, saw, paint tools)", cost: "$500-$1,500" },
      { id: "crew_kits", label: "Crew kits prepared (branded vests, checklists, bags)", cost: "$50-$100/kit" },
    ]
  },
  {
    category: "Technology & Digital (Website)",
    items: [
      { id: "website_live", label: "Website live (moovinforward.org)", cost: "Done" },
      { id: "dns_pointed", label: "DNS pointed to production (Porkbun A/CNAME records)", cost: "Done" },
      { id: "portal_tested", label: "Client Portal tested with real Google login", cost: "Free" },
      { id: "google_analytics", label: "Google Analytics connected — add G-measurement ID to site settings", cost: "Free" },
      { id: "google_search_console", label: "Google Search Console — submit sitemap, monitor indexing & SEO", cost: "Free" },
      { id: "resend_domain", label: "Resend — verify moovinforward.org domain for branded emails (not just Gmail)", cost: "Free" },
      { id: "stripe_live", label: "Stripe — switch from test mode to live mode for real payments", cost: "Free" },
      { id: "ssl_cert", label: "SSL certificate active (https:// working on moovinforward.org)", cost: "Free w/ host" },
    ]
  },
  {
    category: "Business Accounts & Services",
    items: [
      { id: "google_business", label: "Google Business Profile — create & verify (critical for local DFW searches)", cost: "Free" },
      { id: "google_voice", label: "Google Voice — business phone number set up", cost: "Done" },
      { id: "calendly", label: "Calendly — scheduling link for client walkthroughs & consultations", cost: "Done" },
      { id: "porkbun", label: "Porkbun — domain registered and DNS configured", cost: "Done" },
      { id: "google_domain", label: "Google Domain — domain management set up", cost: "Done" },
      { id: "quickbooks", label: "HoneyBook — invoicing, contracts, and bookkeeping (sister manages)", cost: "Done" },
      { id: "yelp_business", label: "Yelp Business Page — claim/create for DFW visibility & reviews", cost: "Free" },
      { id: "thumbtack", label: "Thumbtack Pro — list services for lead generation in DFW", cost: "Pay-per-lead" },
      { id: "linkedin_company", label: "LinkedIn Company Page — professional presence for investors & PMs", cost: "Free" },
      { id: "nextdoor_business", label: "Nextdoor Business Page — neighborhood-level visibility in DFW", cost: "Free" },
      { id: "google_maps", label: "Google Maps listing active (auto from Google Business Profile)", cost: "Free" },
      { id: "canva_pro", label: "Canva — marketing flyers, proposal covers, social media posts", cost: "$13/mo" },
      { id: "docusign", label: "DocuSign or HelloSign — electronic contract signatures from clients", cost: "$10-$15/mo" },
    ]
  },
  {
    category: "Social Media & Marketing",
    items: [
      { id: "instagram", label: "Instagram Business — before/after photos, project reels", cost: "Free" },
      { id: "facebook_business", label: "Facebook Business Page — DFW community groups, local networking", cost: "Free" },
      { id: "tiktok", label: "TikTok Business — before/after transformation videos (optional but powerful)", cost: "Free" },
      { id: "google_ads", label: "Google Ads account — ready to run when budget allows (target 'make ready DFW')", cost: "Pay-per-click" },
      { id: "email_list", label: "Email list started — collect PM/investor contacts for updates", cost: "Free" },
    ]
  },
  {
    category: "Sales & Market Entry",
    items: [
      { id: "business_cards", label: "Business cards printed (name, phone, moovinforward.org)", cost: "$30-$50" },
      { id: "pitch_printed", label: "Investor Pitch PDF printed (3-5 copies for meetings)", cost: "$10" },
      { id: "structure_printed", label: "Business Structure PDF printed for attorney review", cost: "$5" },
      { id: "receivership_research", label: "Tarrant County receivership docket researched (court dates noted)", cost: "Free" },
      { id: "attended_court", label: "Attended 1+ public receivership hearing (observation only)", cost: "Free" },
      { id: "pm_list", label: "List of 10+ target property management companies built", cost: "Free" },
      { id: "first_contact", label: "First outreach to potential client (email, call, or walk-in)", cost: "Free" },
      { id: "first_unit", label: "First unit completed and documented with photos", cost: "Variable" },
    ]
  },
  {
    category: "Crew & Labor",
    items: [
      { id: "first_crew", label: "First crew member onboarded (IC Agreement + COI on file)", cost: "Free" },
      { id: "crew_insurance_verified", label: "Crew GL insurance verified and COI uploaded", cost: "Free" },
      { id: "crew_portal_tested", label: "Crew Portal tested with crew member login", cost: "Free" },
      { id: "payout_method", label: "Crew payout method confirmed (Zelle, Cash, Check)", cost: "Free" },
    ]
  },
];

const LaunchReadinessTab = () => {
  const [checklist, setChecklist] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/launch-checklist`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setChecklist(d.items || {}); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const toggleItem = async (id) => {
    const updated = { ...checklist, [id]: !checklist[id] };
    setChecklist(updated);
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/launch-checklist`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: updated })
      });
    } catch (e) { console.error("Save failed:", e); }
    setSaving(false);
  };

  const totalItems = LAUNCH_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
  const completedItems = Object.values(checklist).filter(Boolean).length;
  const pct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  if (!loaded) return <div className="tab-loading">Loading checklist...</div>;

  return (
    <div className="launch-tab" data-testid="launch-readiness-tab">
      <div className="launch-header">
        <div className="launch-header-text">
          <h2>Launch Readiness Checklist</h2>
          <p>Everything you need before day one. Check items off as you complete them — progress saves automatically.</p>
        </div>
        <div className="launch-progress-ring" data-testid="launch-progress">
          <svg viewBox="0 0 100 100" className="launch-ring-svg">
            <circle cx="50" cy="50" r="44" className="launch-ring-bg" />
            <circle cx="50" cy="50" r="44" className="launch-ring-fill"
              style={{ strokeDasharray: `${pct * 2.76} 276` }} />
          </svg>
          <div className="launch-ring-text">
            <span className="launch-ring-pct">{pct}%</span>
            <span className="launch-ring-label">{completedItems}/{totalItems}</span>
          </div>
        </div>
      </div>
      {saving && <div className="launch-saving">Saving...</div>}

      <div className="launch-categories">
        {LAUNCH_CATEGORIES.map(cat => {
          const catCompleted = cat.items.filter(i => checklist[i.id]).length;
          const catTotal = cat.items.length;
          return (
            <div key={cat.category} className="launch-category" data-testid={`launch-cat-${cat.category.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="launch-cat-header">
                <h3>{cat.category}</h3>
                <span className={`launch-cat-count ${catCompleted === catTotal ? "launch-cat-done" : ""}`}>
                  {catCompleted}/{catTotal}
                </span>
              </div>
              <div className="launch-items">
                {cat.items.map(item => (
                  <label key={item.id} className={`launch-item ${checklist[item.id] ? "launch-item-checked" : ""}`} data-testid={`launch-item-${item.id}`}>
                    <input type="checkbox" checked={!!checklist[item.id]} onChange={() => toggleItem(item.id)} />
                    <span className="launch-check-box"></span>
                    <span className="launch-item-label">{item.label}</span>
                    <span className="launch-item-cost">{item.cost}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <BusinessOpsGuide />
    </div>
  );
};

// ===== Business Operations Guide (Banking, Billing, Pro Accounts) =====
const BIZ_OPS_STEPS = [
  {
    title: "Banking Structure (Relay)",
    steps: [
      "Go to relayfi.com and sign up with your business email",
      "You'll need your EIN to complete the application (get this first from irs.gov)",
      "Once approved, create 4 separate checking accounts under one Relay business account:",
      "  Account 1: MATERIALS — every client materials payment lands here",
      "  Account 2: OPERATING — your Stabilization Fees (rent + 15%) go here",
      "  Account 3: PAYROLL — crew payments come from here",
      "  Account 4: TAX RESERVE — auto-transfer 25-30% of every payment here",
      "Order Relay debit cards for yourself and each crew lead — set spending limits per card",
      "Assign a card (or share a card across a few units) to track per-unit material spend cleanly",
      "Every transaction shows in real-time in the Relay app — no surprises"
    ]
  },
  {
    title: "Connect HoneyBook to Relay",
    steps: [
      "Log into HoneyBook → Settings → Integrations",
      "Connect your Relay OPERATING account as your primary bank for Stabilization Fees",
      "When you send a Stabilization Fee invoice through HoneyBook, payment hits Relay OPERATING",
      "For materials invoices, send a separate HoneyBook invoice (or a Stripe payment link) and route the deposit account to Relay MATERIALS",
      "This keeps materials money separate from labor/fee money — clean books, simple reconciliation"
    ]
  },
  {
    title: "How to Bill a Client for Materials",
    steps: [
      "Shannon walks the unit and does the Walk-Through Calculator (Field Tools tab)",
      "Hit 'Generate Client Invoice' — this builds the itemized product list with retail prices, line by line, per unit",
      "Pick the rail that fits the deal:",
      "  • HoneyBook invoice (recurring or repeat clients)",
      "  • Stripe payment link (one-off / fast close)",
      "  • Direct ACH or wire (institutional clients, larger portfolios)",
      "Client pays → funds land in your Relay MATERIALS account",
      "A Relay debit card (assigned to that unit, or shared across a small batch) is what you swipe at Home Depot Pro, Sherwin Williams, Floor & Decor, etc.",
      "Every unit's material cost stays itemized in the project record — the client only ever sees the agreed line items, not your wholesale cost"
    ]
  },
  {
    title: "Pro Account Discounts",
    steps: [
      "The Product Catalog tab has wholesale prices based on 2026 market averages",
      "Your ACTUAL wholesale price may be different depending on your pro account discount tier",
      "To update: after your first purchase at each store, check your receipt for the actual price you paid",
      "Tell Shannon the actual prices and she'll update them in the system",
      "Stores to track discounts:",
      "  Home Depot Pro Xtra — loyalty points + volume pricing (already set up)",
      "  Sherwin Williams Pro — typically 30-40% off retail (set up your commercial account)",
      "  Lowe's Pro — similar to HD Pro, volume discounts on bulk orders",
      "  Floor & Decor Pro — pro pricing on LVP, tile, and transitions",
      "The bigger your volume, the bigger your discount. As you do more turns, negotiate better pricing"
    ]
  },
  {
    title: "Payment Flow Summary",
    steps: [
      "CLIENT pays materials invoice (HoneyBook / Stripe link / ACH wire) → Relay MATERIALS account",
      "You swipe the unit's assigned Relay debit card at the pro accounts → paid from Relay MATERIALS (margin stays in the account)",
      "CLIENT pays Stabilization Fee (rent + 15%) via HoneyBook → Relay OPERATING account",
      "You pay crew from Relay PAYROLL account",
      "Auto-transfer 25-30% of all income → Relay TAX RESERVE account",
      "End of month: sweep leftover profits from MATERIALS to OPERATING",
      "Track everything in HoneyBook (invoices) and in Relay (banking + per-card spend)"
    ]
  }
];

const BusinessOpsGuide = () => {
  const [activeStep, setActiveStep] = useState(0);
  return (
    <div className="biz-ops-guide" data-testid="biz-ops-guide">
      <h3 className="biz-ops-title">Business Operations Setup Guide</h3>
      <p className="biz-ops-intro">Step-by-step instructions for setting up banking (Relay), connecting HoneyBook, billing clients (HoneyBook / Stripe / ACH wire) into Relay MATERIALS, and managing pro account discounts.</p>
      <div className="biz-ops-tabs">
        {BIZ_OPS_STEPS.map((s, i) => (
          <button key={i} className={`biz-ops-tab ${activeStep === i ? "active" : ""}`} onClick={() => setActiveStep(i)} data-testid={`biz-ops-tab-${i}`}>
            {s.title}
          </button>
        ))}
      </div>
      <div className="biz-ops-content">
        <h4>{BIZ_OPS_STEPS[activeStep].title}</h4>
        <div className="biz-ops-steps">
          {BIZ_OPS_STEPS[activeStep].steps.map((step, i) => (
            <div key={i} className={`cert-step ${step.startsWith("  ") ? "biz-ops-indent" : ""}`}>
              <span className="cert-step-num">{step.startsWith("  ") ? " " : i + 1}</span>
              <span className="cert-step-text">{step.trim()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== COO Welcome Guide =====
const COO_GUIDE_TABS = [
  { tab: "Home", desc: "Your operations overview — active projects, open tasks, launch readiness progress, and the Shared Board for communicating with the CEO." },
  { tab: "Projects", desc: "See all active projects, crew assignments, materials, and project phases. Click any project to see details and update status." },
  { tab: "Tasks", desc: "The team task board — create tasks, assign them, set priorities and due dates. Track status: To Do → In Progress → Done." },
  { tab: "Payroll", desc: "Crew payroll tracking — payout rates per unit, payment methods (Zelle/Cash/Check), and global summary for 1099 reporting." },
  { tab: "Contracts", desc: "Legal templates — MSA, Work Authorization, Subcontractor Agreement, NDA, Independent Contractor Agreement, Business Launch Checklist, Business Structure, and Trademark Filing. Download as PDF." },
  { tab: "Compliance", desc: "Documentation standards, email setup, and compliance procedures to keep the business running clean." },
  { tab: "Launch", desc: "58-item launch readiness checklist across 6 categories — track what's done, what's pending, and estimated costs." },
];

const COOWelcomeGuide = ({ onDismiss }) => (
  <div className="coo-guide-overlay" data-testid="coo-welcome-guide">
    <div className="coo-guide-modal" style={{ maxWidth: 780 }}>
      <h2 style={{ color: "#00FFFF", marginBottom: 6 }}>Hey Shaneka 💜</h2>
      <p className="coo-guide-intro" style={{ fontSize: 15, lineHeight: 1.55 }}>
        I&apos;ve been building nonstop while you were holding it down elsewhere — and I want you to see what your Digital COO actually does for us now. You&apos;re the <strong style={{ color: "#00DC78" }}>Administrator</strong>. Same admin power I have, same clean Cockpit. The system itself is the COO — it delegates, it fires, it watches. We just show up and do the human parts.
      </p>

      <div style={{ background: "linear-gradient(135deg, rgba(0,255,255,0.06) 0%, rgba(0,220,120,0.06) 100%)", border: "1px solid rgba(0,255,255,0.28)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.68rem", letterSpacing: "0.2em", fontWeight: 800, marginBottom: 6 }}>
          WHAT WE DO · ONLY THESE TWO THINGS
        </div>
        <ol style={{ margin: 0, paddingLeft: 20, color: "#E5E7EB", fontSize: 14, lineHeight: 1.6 }}>
          <li><strong>Walk the unit with the camera.</strong> Every capture fires the whole engine in the background.</li>
          <li><strong>Read the answer cards on live calls.</strong> Sidekick tells us exactly what to say.</li>
        </ol>
        <div style={{ color: "#ADBED1", fontSize: 12.5, marginTop: 8, fontStyle: "italic" }}>
          Everything else — the system rolls it over. Not us.
        </div>
      </div>

      <div className="coo-guide-list">
        <div className="coo-guide-item" style={{ background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#00DC78" }}>🎯 Field Intake Engine (auto)</strong>
          <p>You snap a photo of a room. The backend evaluates it against <strong>five compliance doctrines</strong> at once — NSPIRE, Section 8, HUD-VASH, City Code, and Coalition Standards. It flags violations, tags water/gas/electrical zones with <strong>MAINTENANCE_ESCORT_REQUIRED</strong>, catches missing fixtures (medicine cabinets, pantries, grab-bar walls, fire extinguishers, under-sink plumbing), and picks the right variation (V1 · V2 Tier 2 · V3 HUD-VASH). No cap on photos. No cookie-cutter templates.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(255,179,71,0.06)", border: "1px solid rgba(255,179,71,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#FFB347" }}>💰 Banking Routing (auto)</strong>
          <p>Every proposal returns the full <strong>50/50 flow</strong>: owner covers 100% procurement at contract execution → vendors get paid; then the stabilization fee <code>(rent × 3) × 1.20</code> lands in Moovin Forward operating, pays remaining vendors outright, and the residue is our firm margin. If a deal is underwater at the prospect&apos;s rent, the system flashes a warning with the break-even rent so we never sign blind.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#A78BFA" }}>🏛 MWBE / WOSB Application Engine (auto)</strong>
          <p>Twelve startup-friendly certifications sit in a queue — TX-HUB, SBA-WOSB, SBA 8(a), EDWOSB, WBENC, NMSDC, HUD Section 3, HUD-VASH, DFW-MSDC, Dallas BID/BEP, Fort Worth M/WBE, TUCP DBE. Zero revenue barrier. Our business profile + 7 NAICS codes + all documents already pre-attached. When an award comes through, the system alerts us.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(0,255,255,0.06)", border: "1px solid rgba(0,255,255,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#00FFFF" }}>📞 Telephony Sidekick (auto)</strong>
          <p>When our Twilio number rings, the sidekick auto-registers the call and looks up the caller. We pick up, they talk, and the backend transcribes what they say → generates the <strong>exact next sentence</strong> for us to read aloud, in Shannon&apos;s voice. In parallel, it drafts the follow-up proposal + outreach text so by the time we hang up, both are ready to send.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(90,200,216,0.06)", border: "1px solid rgba(90,200,216,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#5AC8D8" }}>🎣 Outreach Scrape (auto every 6h)</strong>
          <p>Continuous scanning across receivership dockets, REO bank portfolios, HUD-VASH landlord networks, DFW multifamily operators, and court-appointed attorneys. Pre-composed proposal snippets in our voice. When a lead books a walkthrough, the system alerts us.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(148,163,184,0.06)", border: "1px solid rgba(148,163,184,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#94A3B8" }}>🔍 Runtime Self-Diagnostic (auto every 15m)</strong>
          <p>Six critical surfaces (design studio, field intake, telephony, voice webhook, property intel, more) get pinged every quarter-hour. If two consecutive sweeps come back degraded, the system pages us. One hiccup never bothers us — persistence is the trigger.</p>
        </div>

        <div className="coo-guide-item" style={{ background: "rgba(252,165,165,0.06)", border: "1px solid rgba(252,165,165,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
          <strong style={{ color: "#FCA5A5" }}>🔔 Executive Notification (only when it matters)</strong>
          <p>Twelve event types EVER reach our phones: contract won · contract lost · positive/negative review · walkthrough request · partnership inquiry · on-site friction · safety incident · MWBE certification awarded · warranty claim · vendor dropout · diagnostic failure. Everything else the COO handles silently.</p>
        </div>

        <div className="coo-guide-item" style={{ padding: "1rem 1.1rem" }}>
          <strong style={{ color: "#fff" }}>🤝 Trade Vendor Consent Funnel</strong>
          <p>On the Home tab you&apos;ll see a &quot;<strong>+ Add Vendor</strong>&quot; button on the Consent Funnel card. One tap opens the invitation form. Every vendor we onboard from there gets our OCC Bulletin 2013-29 vendor profile + Master Vendor Services Agreement pre-attached.</p>
        </div>

        <div className="coo-guide-item" style={{ padding: "1rem 1.1rem" }}>
          <strong style={{ color: "#fff" }}>📋 What we do together on the dashboard</strong>
          <p>Same tabs as me. Same tools. Cockpit · Design + Projects · Vendor Network · Money · Advanced. You can read anything I&apos;m reading, respond to shared items on the Home tab, work prospects in parallel. If you spot something and want my eyes, drop it in the Shared Board on Home.</p>
        </div>
      </div>

      <div style={{ background: "linear-gradient(180deg, rgba(0,255,255,0.05) 0%, transparent 100%)", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 8, padding: "12px 14px", marginTop: 12, marginBottom: 8 }}>
        <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", letterSpacing: "0.18em", fontWeight: 800, marginBottom: 6 }}>
          THE MOAT · WHY THIS MATTERS
        </div>
        <p style={{ color: "#E5E7EB", fontSize: 13.5, lineHeight: 1.55, margin: 0 }}>
          Moovin Forward is an <strong>ROI Stabilization Firm</strong>. Every capture, every call, every proposal is one continuous flow — no silos, no fragmentation. We built this to stand on four pillars: <strong style={{ color: "#00DC78" }}>profitability</strong> · <strong style={{ color: "#FFB347" }}>job creation</strong> · <strong style={{ color: "#E8B8C4" }}>push love</strong> · <strong style={{ color: "#A78BFA" }}>build community</strong>. That&apos;s the North Star. Everything the system does serves those four.
        </p>
      </div>

      <button onClick={onDismiss} className="coo-guide-btn" data-testid="coo-guide-dismiss" style={{ background: "linear-gradient(90deg, #00DC78 0%, #00B364 100%)", color: "#03151F", fontWeight: 800 }}>
        Got It — Let&apos;s Go 💜
      </button>
    </div>
  </div>
);

// ===== Main Dashboard =====
// Tab definitions per dashboard role
const ALL_TABS = [
  { key: "home", label: "Home", homeStyle: true },
  { key: "divider1", divider: true },
  { key: "projects", label: "Projects" },
  { key: "tasks", label: "Tasks" },
  { key: "clients", label: "Clients" },
  { key: "payroll", label: "Payroll" },
  { key: "schedule", label: "Schedule" },
  { key: "divider2", divider: true },
  { key: "contracts", label: "Contracts" },
  { key: "prospects", label: "Prospects" },
  { key: "tools", label: "Field Tools" },
  { key: "operations", label: "Operations" },
  { key: "compliance", label: "Compliance" },
  { key: "launch", label: "Launch" },
  { key: "revenue", label: "Revenue" },
];

// CEO: Business development, prospecting, clients, operations, revenue
const CEO_TABS = ["home", "divider1", "projects", "clients", "divider2", "contracts", "prospects", "tools", "operations", "schedule", "revenue"];
// COO: Operations, compliance, payroll, tasks, legal, launch, schedule + field tools
const COO_TABS = ["home", "divider1", "projects", "tasks", "payroll", "schedule", "divider2", "contracts", "prospects", "tools", "compliance", "launch"];


const RevenueTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/revenue-stats`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null).then(setStats);
  }, []);
  if (!stats) return <div className="dash-loading">Loading revenue data...</div>;
  return (
    <div className="dash-tab-content" data-testid="revenue-tab">
      <h2>Revenue Dashboard</h2>
      <div className="revenue-grid">
        <div className="revenue-card"><span className="revenue-val">{stats.total_projects}</span><span className="revenue-label">Total Projects</span></div>
        <div className="revenue-card"><span className="revenue-val">{stats.completed_projects}</span><span className="revenue-label">Completed</span></div>
        <div className="revenue-card"><span className="revenue-val">{stats.active_projects}</span><span className="revenue-label">Active</span></div>
        <div className="revenue-card revenue-card-money"><span className="revenue-val">${stats.total_paid.toLocaleString()}</span><span className="revenue-label">Revenue Collected</span></div>
        <div className="revenue-card"><span className="revenue-val">${stats.outstanding.toLocaleString()}</span><span className="revenue-label">Outstanding</span></div>
        <div className="revenue-card"><span className="revenue-val">${stats.avg_material_cost.toLocaleString()}</span><span className="revenue-label">Avg Material Cost/Unit</span></div>
      </div>
      {Object.keys(stats.monthly_revenue).length > 0 && (
        <div className="revenue-monthly">
          <h3>Monthly Revenue</h3>
          {Object.entries(stats.monthly_revenue).sort().map(([month, amt]) => (
            <div key={month} className="revenue-month-row"><span>{month}</span><span className="revenue-month-amt">${amt.toLocaleString()}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};


const CrewScheduleTab = () => {
  const [events, setEvents] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ crew_name: "", crew_position: "General", project_address: "", date: "", start_time: "09:00", end_time: "17:00", recurrence: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const [evR, crR, prR] = await Promise.all([
      fetch(`${BACKEND_URL}/api/admin/crew-schedule`, { credentials: "include" }),
      fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: "include" }),
      fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" }),
    ]);
    if (evR.ok) setEvents(await evR.json());
    if (crR.ok) {
      const all = await crR.json();
      setCrewMembers(all.filter(u => u.role === "crew"));
    }
    if (prR.ok) setProjects(await prR.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.crew_name || !form.date) return;
    setAdding(true);
    await fetch(`${BACKEND_URL}/api/admin/crew-schedule`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(form)
    });
    setForm(f => ({ ...f, crew_name: "", project_address: "", date: "", notes: "" }));
    setAdding(false);
    load();
  };

  const remove = async (id) => {
    await fetch(`${BACKEND_URL}/api/admin/crew-schedule/${id}`, { method: "DELETE", credentials: "include" });
    load();
  };

  const grouped = {};
  events.forEach(e => { const d = e.date || "Unscheduled"; if (!grouped[d]) grouped[d] = []; grouped[d].push(e); });

  return (
    <div className="dash-tab-content" data-testid="crew-schedule-tab">
      <h2>Crew Schedule</h2>
      <p className="ops-lead">Assign crew to properties. Select recurring schedules for regular cleaning or maintenance.</p>

      <form onSubmit={add} className="schedule-form-grid" data-testid="schedule-form">
        <div className="schedule-field">
          <label>Team Member</label>
          {crewMembers.length > 0 ? (
            <select value={form.crew_name} onChange={e => setForm(f => ({...f, crew_name: e.target.value}))} className="schedule-select" data-testid="schedule-crew" required>
              <option value="">Select crew...</option>
              {crewMembers.map(c => (
                <option key={c.user_id} value={c.name || c.email}>{c.name || c.email} ({c.crew_type || "general"})</option>
              ))}
              <option value="__custom__">-- Type name manually --</option>
            </select>
          ) : (
            <input type="text" placeholder="Crew member name" value={form.crew_name} onChange={e => setForm(f => ({...f, crew_name: e.target.value}))} className="schedule-input" data-testid="schedule-crew" required />
          )}
          {form.crew_name === "__custom__" && (
            <input type="text" placeholder="Enter name" onChange={e => setForm(f => ({...f, crew_name: e.target.value}))} className="schedule-input" style={{marginTop: "0.3rem"}} />
          )}
        </div>

        <div className="schedule-field">
          <label>Position</label>
          <select value={form.crew_position} onChange={e => setForm(f => ({...f, crew_position: e.target.value}))} className="schedule-select" data-testid="schedule-position">
            {CREW_POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="schedule-field">
          <label>Property / Address</label>
          {projects.length > 0 ? (
            <select value={form.project_address} onChange={e => setForm(f => ({...f, project_address: e.target.value}))} className="schedule-select" data-testid="schedule-address" required>
              <option value="">Select property...</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.address || p.name}>{p.address || p.name} {p.unit_number ? `#${p.unit_number}` : ""}</option>
              ))}
              <option value="__custom__">-- Type address manually --</option>
            </select>
          ) : (
            <input type="text" placeholder="Property address" value={form.project_address} onChange={e => setForm(f => ({...f, project_address: e.target.value}))} className="schedule-input" data-testid="schedule-address" required />
          )}
          {form.project_address === "__custom__" && (
            <input type="text" placeholder="Enter address" onChange={e => setForm(f => ({...f, project_address: e.target.value}))} className="schedule-input" style={{marginTop: "0.3rem"}} />
          )}
        </div>

        <div className="schedule-field">
          <label>Date</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="schedule-input" data-testid="schedule-date" required />
        </div>

        <div className="schedule-field">
          <label>Start Time</label>
          <input type="time" value={form.start_time} onChange={e => setForm(f => ({...f, start_time: e.target.value}))} className="schedule-input" data-testid="schedule-start-time" required />
        </div>

        <div className="schedule-field">
          <label>End Time</label>
          <input type="time" value={form.end_time} onChange={e => setForm(f => ({...f, end_time: e.target.value}))} className="schedule-input" data-testid="schedule-end-time" required />
        </div>

        <div className="schedule-field">
          <label>Recurring</label>
          <select value={form.recurrence} onChange={e => setForm(f => ({...f, recurrence: e.target.value}))} className="schedule-select" data-testid="schedule-recurrence">
            {RECURRENCE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div className="schedule-field">
          <label>Notes</label>
          <input type="text" placeholder="Special instructions..." value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="schedule-input" />
        </div>

        <button type="submit" disabled={adding} className="schedule-add-btn" data-testid="schedule-add-btn">{adding ? "Adding..." : "Add to Schedule"}</button>
      </form>

      {Object.entries(grouped).sort().map(([date, evts]) => (
        <div key={date} className="schedule-day">
          <h3 className="schedule-date-header">{date}</h3>
          {evts.map(e => (
            <div key={e.event_id} className="schedule-event" data-testid={`event-${e.event_id}`}>
              <div className="schedule-event-main">
                <strong>{e.crew_name}</strong>
                {e.crew_position && <span className="schedule-position-badge">{e.crew_position}</span>}
                <span className="schedule-event-addr">@ {e.project_address}</span>
              </div>
              <div className="schedule-event-meta">
                {(e.start_time || e.end_time) && (
                  <span className="schedule-time-badge">{e.start_time || "?"} — {e.end_time || "?"}</span>
                )}
                {e.recurrence && <span className="schedule-recurrence-badge">{e.recurrence}</span>}
                {e.notes && <span className="schedule-notes">{e.notes}</span>}
                <button onClick={() => remove(e.event_id)} className="schedule-remove-btn">Remove</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {events.length === 0 && <p className="dash-empty">No scheduled events. Assign crew above to start building your schedule.</p>}
    </div>
  );
};


export { OperationsTab, ComplianceTab, QuickStartWelcome, PayrollTab, TaskBoardTab, LaunchReadinessTab, COOWelcomeGuide, RevenueTab, CrewScheduleTab, LAUNCH_CATEGORIES };
